// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

import { costCents, formatUsd, REPLY_PRIMING, TOKENS_PER_MESSAGE } from './core/count';
import { csvBlob } from './core/csv';
import { bucketLabel, histogramMax } from './core/totals';
import { EXCLUDED, TABLE_CHECKED, type ModelEntry } from './data/models';
import type { PriceEntry } from './data/prices';
import { ROLES, type OverLimitRow, type Totals } from './types';

export interface ReportInput {
  readonly fileName: string;
  readonly fileBytes: number;
  readonly model: ModelEntry;
  readonly tokenizerDescribe: string;
  readonly limit: number | null;
  readonly epochs: number;
  readonly price: PriceEntry | null;
  readonly priceOverride: number | null;
  readonly totals: Totals;
  readonly overLimit: readonly OverLimitRow[];
  readonly truncatedByCap: number;
}

const num = (n: number): string => n.toLocaleString('en-US');

export function bytesLabel(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1048576).toFixed(1)} MB`;
  return `${(n / 1073741824).toFixed(2)} GB`;
}

/* ── over-limit.jsonl — the work queue ────────────────────────────────────── */

/**
 * The rows that break the limit, as a file you can open and fix.
 *
 * This is the artefact. Everything else on the page is a number; this is the
 * thing you act on. Rows are written in their ORIGINAL form, byte for byte, so
 * the file is valid JSONL that a script can read straight back.
 */
export function overLimitJsonl(rows: readonly OverLimitRow[]): Blob {
  const parts: string[] = [];
  for (const row of rows) {
    if (row.text !== null) parts.push(row.text, '\n');
  }
  return new Blob(parts, { type: 'application/x-ndjson' });
}

export function overLimitCsv(rows: readonly OverLimitRow[], limit: number): Blob {
  return csvBlob(
    ['row', 'tokens', 'limit', 'tokens_deleted', 'assistant_tokens_deleted', 'bytes', 'text_exported'],
    rows.map((r) => [r.ordinal, r.tokens, limit, r.truncated, r.assistantLost, r.length, r.text !== null]),
  );
}

/* ── token-stats.csv ──────────────────────────────────────────────────────── */

export function statsCsv(input: ReportInput): Blob {
  const { totals, limit, epochs } = input;
  const billable = totals.cappedTokens * epochs;
  const rows: (string | number | boolean | null)[][] = [
    ['records', totals.rows],
    ['blank lines skipped', totals.blankLines],
    ['messages', totals.messages],
    ['bytes read', totals.bytes],
    ['tokens (content + chat framing)', totals.tokens],
    ['tokens (message content only)', totals.contentTokens],
    ['longest record, tokens', totals.longest],
    ['shortest record, tokens', totals.rows === 0 ? 0 : totals.shortest],
    ['mean tokens per record', totals.rows === 0 ? 0 : Math.round(totals.tokens / totals.rows)],
    ['training example limit', limit ?? 'not set'],
    ['records over the limit', limit === null ? 'not measured' : totals.overLimit],
    ['tokens deleted by truncation', limit === null ? 'not measured' : totals.truncatedTokens],
    ['epochs', epochs],
    ['billable tokens (capped per record, x epochs)', billable],
  ];
  for (let i = 0; i < ROLES.length; i++) {
    rows.push([`content tokens, role=${ROLES[i]}`, totals.byRole[i]]);
  }
  for (const [key, count] of Object.entries(totals.problems)) {
    if (count > 0) rows.push([`records with problem: ${key}`, count]);
  }
  rows.push(['model', input.model.label]);
  rows.push(['tokenizer', input.tokenizerDescribe]);
  rows.push(['price table checked', TABLE_CHECKED]);
  return csvBlob(['measure', 'value'], rows);
}

/* ── report.html ──────────────────────────────────────────────────────────── */

/**
 * A self-contained report.
 *
 * Built entirely with `createElement` and `textContent`. Never string templating,
 * and never `innerHTML`: the file name and the excerpted rows come from a file
 * epochcost did not write, and the report is opened from `file://` where a script
 * would run with no origin to constrain it. There is no `<script>`, no `<link>`
 * and no `<src>` in the output, and a build gate asserts it.
 */
export function reportHtml(input: ReportInput): Blob {
  const doc = document.implementation.createHTMLDocument('epochcost report');
  const { totals, limit, epochs, model } = input;

  const el = <K extends keyof HTMLElementTagNameMap>(
    tag: K,
    text?: string,
    className?: string,
  ): HTMLElementTagNameMap[K] => {
    const node = doc.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (className) node.className = className;
    return node;
  };

  const style = doc.createElement('style');
  style.textContent = `
    :root { color-scheme: light; }
    body { font: 15px/1.6 -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif;
           margin: 0 auto; max-width: 52rem; padding: 2.5rem 1.25rem 4rem; color: #16202e; background: #fff; }
    h1 { font-size: 1.5rem; margin: 0 0 .25rem; }
    h2 { font-size: 1.05rem; margin: 2.25rem 0 .5rem; border-bottom: 1px solid #e2e8f0; padding-bottom: .35rem; }
    .sub { color: #55627a; margin: 0 0 2rem; }
    table { border-collapse: collapse; width: 100%; font-size: .875rem; }
    th, td { text-align: left; padding: .4rem .6rem; border-bottom: 1px solid #eef2f7; }
    th { color: #55627a; font-weight: 600; }
    td.n, th.n { text-align: right; font-variant-numeric: tabular-nums; }
    .big { font-size: 2rem; font-weight: 650; font-variant-numeric: tabular-nums; }
    .bad { color: #b42318; }
    .money { color: #92400e; }
    .caveat { background: #f8fafc; border-left: 3px solid #cbd5e1; padding: .75rem 1rem; margin: 1rem 0; font-size: .875rem; }
    .bar { background: #1f6f5c; height: .7rem; display: inline-block; vertical-align: middle; border-radius: 2px; }
    footer { margin-top: 3rem; color: #55627a; font-size: .8125rem; border-top: 1px solid #e2e8f0; padding-top: 1rem; }
  `;
  doc.head.appendChild(style);

  const meta = doc.createElement('meta');
  meta.setAttribute('name', 'viewport');
  meta.setAttribute('content', 'width=device-width, initial-scale=1');
  doc.head.appendChild(meta);

  const body = doc.body;
  body.appendChild(el('h1', 'epochcost report'));
  body.appendChild(
    el('p', `${input.fileName} — ${bytesLabel(input.fileBytes)}, counted with ${input.tokenizerDescribe}`, 'sub'),
  );

  /* headline */
  if (limit !== null) {
    const headline = el('p', '', 'big');
    if (totals.overLimit > 0) {
      headline.className = 'big bad';
      headline.textContent = `${num(totals.overLimit)} of ${num(totals.rows)} records exceed the ${num(limit)}-token training limit`;
      body.appendChild(headline);
      body.appendChild(
        el(
          'p',
          `${num(totals.truncatedTokens)} tokens sit past the limit. Truncation removes tokens from the end of a ` +
            `record, and the end of a chat record is the assistant's answer.`,
        ),
      );
    } else {
      headline.textContent = `No record exceeds the ${num(limit)}-token training limit`;
      body.appendChild(headline);
    }
  }

  /* cost */
  const price = input.priceOverride ?? input.price?.perMillion ?? null;
  if (price !== null && limit !== null) {
    const billable = totals.cappedTokens * epochs;
    const cents = costCents(billable, price);
    body.appendChild(el('h2', 'Cost'));
    const cost = el('p', '', 'big money');
    cost.textContent = `${formatUsd(cents)} for ${epochs} epoch${epochs === 1 ? '' : 's'}`;
    body.appendChild(cost);
    body.appendChild(
      el(
        'p',
        `${num(billable)} billable tokens at $${price}/1M. Each record is billed at the lesser of its own ` +
          `length and the ${num(limit)}-token limit, because the provider truncates before it trains.`,
      ),
    );
    if (input.price) {
      body.appendChild(
        el('p', `${input.price.provider} — ${input.price.label}. Price read from ${input.price.source} on ${TABLE_CHECKED}.`, 'caveat'),
      );
      if (input.price.billsValidation) {
        body.appendChild(
          el(
            'p',
            'This provider also bills tokens in your validation file, multiplied by the number of evaluations. ' +
              'That file is not counted here.',
            'caveat',
          ),
        );
      }
      if (input.price.minimumUsd !== undefined && cents < input.price.minimumUsd * 100) {
        body.appendChild(
          el('p', `Below this provider's $${input.price.minimumUsd} minimum charge per job, so you would pay the minimum.`, 'caveat'),
        );
      }
    }
  }

  /* distribution */
  body.appendChild(el('h2', 'Record lengths'));
  const max = histogramMax(totals.histogram);
  const table = doc.createElement('table');
  const thead = doc.createElement('tr');
  for (const [label, cls] of [
    ['Tokens', ''],
    ['Records', 'n'],
    ['', ''],
  ] as const) {
    const th = el('th', label, cls);
    thead.appendChild(th);
  }
  table.appendChild(thead);
  for (let i = 0; i < totals.histogram.counts.length; i++) {
    const count = totals.histogram.counts[i];
    if (count === 0) continue;
    const tr = doc.createElement('tr');
    tr.appendChild(el('td', bucketLabel(totals.histogram.edges, i)));
    tr.appendChild(el('td', num(count), 'n'));
    const barCell = doc.createElement('td');
    const bar = el('span', '', 'bar');
    bar.style.width = `${Math.max(2, Math.round((count / max) * 260))}px`;
    if (limit !== null && totals.histogram.edges[i] > limit) bar.style.background = '#b42318';
    barCell.appendChild(bar);
    tr.appendChild(barCell);
    table.appendChild(tr);
  }
  body.appendChild(table);

  /* totals */
  body.appendChild(el('h2', 'Totals'));
  const totalsTable = doc.createElement('table');
  const rows: [string, string][] = [
    ['Records', num(totals.rows)],
    ['Messages', num(totals.messages)],
    ['Tokens, content plus chat framing', num(totals.tokens)],
    ['Tokens, message content only', num(totals.contentTokens)],
    ['Longest record', `${num(totals.longest)} tokens`],
    ['Shortest record', `${num(totals.rows === 0 ? 0 : totals.shortest)} tokens`],
    ['Mean per record', `${num(totals.rows === 0 ? 0 : Math.round(totals.tokens / totals.rows))} tokens`],
  ];
  for (let i = 0; i < ROLES.length; i++) {
    if (totals.byRole[i] > 0) rows.push([`Content tokens, ${ROLES[i]}`, num(totals.byRole[i])]);
  }
  if (totals.blankLines > 0) rows.push(['Blank lines skipped', num(totals.blankLines)]);
  for (const [key, count] of Object.entries(totals.problems)) {
    if (count > 0) rows.push([`Records with a problem: ${key}`, num(count)]);
  }
  for (const [label, value] of rows) {
    const tr = doc.createElement('tr');
    tr.appendChild(el('td', label));
    tr.appendChild(el('td', value, 'n'));
    totalsTable.appendChild(tr);
  }
  body.appendChild(totalsTable);

  /* what this does and does not mean */
  body.appendChild(el('h2', 'What these numbers are'));
  const notes = doc.createElement('ul');
  const note = (text: string): void => {
    const li = doc.createElement('li');
    li.textContent = text;
    notes.appendChild(li);
  };
  note(
    `The token counts come from ${model.label}'s own tokenizer, so the content half is exact rather than estimated ` +
      `from a characters-per-token ratio.`,
  );
  note(
    `The chat framing — ${TOKENS_PER_MESSAGE} tokens per message plus ${REPLY_PRIMING} priming the reply — is ` +
      `documented accounting from OpenAI's cookbook, not a published specification, and OpenAI has never published ` +
      `it for the gpt-4.1 family. Treat the framing component as documented rather than guaranteed.`,
  );
  note(
    `The limit measured against is the maximum length of one TRAINING EXAMPLE, which is not the model's context ` +
      `window. For the gpt-4.1 and gpt-4o families it is 65,536 tokens against a 128,000-token window — half. ` +
      `Checking a dataset against the context window passes records the trainer will still cut.`,
  );
  note(
    'This report describes the file it was given. It does not say the file is ready, correct, or safe to train on.',
  );
  notes.childNodes.length > 0 && body.appendChild(notes);

  if (input.truncatedByCap > 0) {
    body.appendChild(
      el(
        'p',
        `${num(input.truncatedByCap)} over-limit records were counted but not exported, because the exported work ` +
          `queue is bounded. The counts above include all of them.`,
        'caveat',
      ),
    );
  }

  const excluded = doc.createElement('p');
  excluded.className = 'caveat';
  excluded.textContent =
    'Models epochcost cannot count: ' + EXCLUDED.map((e) => e.label).join('; ') + '. See the tool for why.';
  body.appendChild(excluded);

  const footer = doc.createElement('footer');
  footer.textContent =
    `Produced by epochcost — epochcost.benrichardson.dev — entirely in the browser; the file was not uploaded. ` +
    `Price and limit tables checked ${TABLE_CHECKED}.`;
  body.appendChild(footer);

  return new Blob(['<!doctype html>\n', doc.documentElement.outerHTML], { type: 'text/html;charset=utf-8' });
}
